import type { AdvancedOrderProvider } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import {
  DEFINITIVE_PLATFORM_FEE_BPS,
  getLimitOrderIntegratorFeeBps,
} from '@/utils/advancedOrderFees';

type TooltipLimitOrderFeeProps = {
  chainId: number;
  isStableToStablePair: boolean;
  provider?: AdvancedOrderProvider;
};

export const TooltipLimitOrderFee = ({
  chainId,
  isStableToStablePair,
  provider,
}: TooltipLimitOrderFeeProps) => {
  const integratorFeeFormatted = (
    getLimitOrderIntegratorFeeBps(isStableToStablePair) / 10_000
  ).toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 2 });
  const networkFeeLabel = `${NETWORK_NAME_PER_CHAIN_ID[chainId]} fee`;

  let feeRows: { label: string; value: string; valueClass?: string }[];
  let tooltipContent: string;
  if (provider === 'definitive') {
    feeRows = [
      { label: 'Matcha fee', value: integratorFeeFormatted },
      {
        label: 'Execution provider fee',
        value: (DEFINITIVE_PLATFORM_FEE_BPS / 10_000).toLocaleString(undefined, {
          style: 'percent',
          maximumFractionDigits: 2,
        }),
      },
      { label: networkFeeLabel, value: 'Variable' },
    ];
    tooltipContent =
      'Fees are deducted when the order fills. The Buy amount already reflects them; the total is an estimate and the final fill may differ.';
  } else {
    feeRows = [
      { label: 'Indirect fee', value: integratorFeeFormatted },
      { label: networkFeeLabel, value: 'Free', valueClass: 'text-token-green-1100' },
    ];
    tooltipContent = `Matcha adjusts the taker rate by ${integratorFeeFormatted} for this pair instead of charging a direct fee or reducing your output. 
  This may affect fills, so consider adjusting your rate for better execution.`;
  }

  return (
    <div className="w-[248px] flex flex-col gap-y-2.5 whitespace-nowrap">
      {feeRows.map((row) => (
        <div
          key={row.label}
          className="text-token-gray-1600 items-center flex justify-between w-full"
        >
          <div className={fontStyles({ variant: 'BODY_SM', class: ['text-token-gray-900'] })}>
            {row.label}
          </div>
          <div
            className={fontStyles({
              variant: 'BODY_SM',
              class: ['items-center flex text-right', row.valueClass],
            })}
          >
            {row.value}
          </div>
        </div>
      ))}
      <div className="text-token-gray-1600 items-center flex justify-between w-full border-t border-t-special-border-divider pt-2.5">
        <div
          className={fontStyles({
            variant: 'BODY_SM',
            class: ['text-token-gray-1600 whitespace-normal'],
          })}
        >
          {tooltipContent}
        </div>
      </div>
    </div>
  );
};
